// Configuration service for dynamic backend switching
export class ConfigService {
  constructor() {
    this.init();
  }

  init() {
    // Get URL parameters
    const urlParams = new URLSearchParams(window.location.search);
    const pathParts = window.location.pathname.split('/').filter(Boolean);
    
    // Check for backend configuration in various ways
    this.backendType = this.determineBackendType(urlParams, pathParts);
    this.config = this.getBackendConfig(this.backendType);
    
    console.log('ConfigService initialized:', {
      backendType: this.backendType,
      backendName: this.config.name,
      apiUrl: this.config.apiBaseUrl,
      wsUrl: this.config.websocketUrl,
      storedBackend: localStorage.getItem('polyglot_backend'),
      hostname: window.location.hostname
    });
  }

  determineBackendType(urlParams, pathParts) {
    // Method 1: Query parameter (?backend=local or ?backend=hf)
    // Highest priority - allows temporary override for testing
    const backendParam = urlParams.get('backend');
    if (backendParam) {
      console.log('[ConfigService] Backend determined by query parameter:', backendParam);
      // Support all 4 backend types
      if (backendParam === 'local' || backendParam === 'localhost') {
        return 'local';
      }
      if (backendParam === 'local_test') {
        return 'local_test';
      }
      if (backendParam === 'primary-endpoint' || backendParam === 'hf_test' || backendParam === 'huggingface_test') {
        return 'primary-endpoint';
      }
      if (backendParam === 'secondary-endpoint' || backendParam === 'hf' || backendParam === 'huggingface') {
        return 'secondary-endpoint';
      }
    }

    // Method 2: localStorage setting (for persistent user preference)
    // Second highest priority - user's saved preference should override automatic detection
    const storedBackend = localStorage.getItem('polyglot_backend');
    if (storedBackend) {
      if (storedBackend === 'local' || storedBackend === 'local_test' ||
          storedBackend === 'primary-endpoint' || storedBackend === 'secondary-endpoint') {
        console.log('[ConfigService] Backend determined by localStorage:', storedBackend);
        return storedBackend;
      } else {
        console.warn('[ConfigService] Unknown stored backend type:', storedBackend, '- clearing and falling back to primary-endpoint');
        localStorage.removeItem('polyglot_backend');
        return 'primary-endpoint';
      }
    }

    // Method 3: Path-based routing (/local/* or /dev/*)
    if (pathParts.includes('local') || pathParts.includes('dev') || pathParts.includes('localhost')) {
      console.log('[ConfigService] Backend determined by path-based routing: local');
      return 'local';
    }

    // Method 4: Subdomain detection (local.yourapp.com)
    // Note: When on localhost without a saved preference, default to 'local' backend
    const hostname = window.location.hostname;
    if (hostname.startsWith('local.') || hostname.startsWith('dev.') || hostname === 'localhost') {
      console.log('[ConfigService] Backend determined by hostname detection: primary-endpoint (hostname=' + hostname + ')');
      return 'primary-endpoint';
    }

    // Method 5: Environment variable override
    const envBackend = import.meta.env.VITE_BACKEND_TYPE;
    if (envBackend === 'local') {
      console.log('[ConfigService] Backend determined by environment variable: local');
      return 'local';
    }

    // Default to primary endpoint
    console.log('[ConfigService] Backend determined by default: primary-endpoint');
    return 'primary-endpoint';
  }

  getBackendConfig(backendType) {
    const configs = {
      local: {
        name: 'Local',
        apiBaseUrl: 'http://localhost:7860/api',
        websocketUrl: 'http://localhost:7860',
        authType: 'optional',
        requiresHfToken: false,
        corsEnabled: true
      },
      local_test: {
        name: 'Local (test)',
        apiBaseUrl: 'http://localhost:7860/api',
        websocketUrl: 'http://localhost:7860',
        authType: 'optional',
        requiresHfToken: false,
        corsEnabled: true
      },
      'primary-endpoint': {
        name: 'Primary',
        apiBaseUrl: 'https://polyglot.tafiti-api.org/api',
        websocketUrl: 'https://polyglot.tafiti-api.org',
        authType: 'optional',
        requiresHfToken: false,
        corsEnabled: false
      },
      'secondary-endpoint': {
        name: 'Secondary',
        apiBaseUrl: 'https://podium.tafiti-api.org/api',
        websocketUrl: 'https://podium.tafiti-api.org',
        authType: 'optional',
        requiresHfToken: false,
        corsEnabled: false
      },
      'secondary-endpoint-2': {
        name: 'Secondary',
        apiBaseUrl: 'https://milimani.tafiti-api.org/api',
        websocketUrl: 'https://milimani.tafiti-api.org',
        authType: 'optional',
        requiresHfToken: false,
        corsEnabled: false
      }
    };

    return configs[backendType] || configs['primary-endpoint'];
  }

  // Getters for easy access
  getApiBaseUrl() {
    return this.config.apiBaseUrl;
  }

  getWebSocketUrl() {
    return this.config.websocketUrl;
  }

  getAuthType() {
    return this.config.authType;
  }

  requiresHfToken() {
    return this.config.requiresHfToken;
  }

  isLocal() {
    return this.backendType === 'local';
  }

  isHuggingFace() {
    return this.backendType === 'primary-endpoint' || this.backendType === 'secondary-endpoint';
  }

  getBackendName() {
    return this.config.name;
  }

  // Probe the primary endpoint on startup; fall back to secondary if it does not
  // respond within 20 seconds, polling every 5 seconds.
  // Call once on app startup before making API/WebSocket connections.
  async resolveActiveEndpoint() {
    if (this.backendType === 'local' || this.backendType === 'local_test') {
      return this.backendType;
    }

    // Respect explicit user selection from settings/query/localStorage.
    // Only run primary failover probing when currently set to primary-endpoint.
    if (this.backendType !== 'primary-endpoint') {
      console.log(`[ConfigService] Backend explicitly set to ${this.backendType}; skipping primary endpoint probe`);
      return this.backendType;
    }

    const primaryConfig = this.getBackendConfig('primary-endpoint');
    const healthUrl = `${primaryConfig.apiBaseUrl}/health`;
    const timeoutMs = 20000;
    const intervalMs = 5000;
    const start = Date.now();

    console.log('[ConfigService] Probing primary endpoint:', healthUrl);

    while (true) {
      try {
        const resp = await fetch(healthUrl, {
          method: 'GET',
          signal: AbortSignal.timeout(4000)
        });
        if (resp.ok) {
          console.log('[ConfigService] Primary endpoint healthy, using primary-endpoint');
          this._applyBackend('primary-endpoint');
          return 'primary-endpoint';
        }
      } catch (_) {
        // not ready yet
      }

      const elapsed = Date.now() - start;
      if (elapsed + intervalMs >= timeoutMs) break;
      console.log(`[ConfigService] Primary not ready (${Math.round(elapsed / 1000)}s elapsed), retrying in ${intervalMs / 1000}s...`);
      await new Promise(r => setTimeout(r, intervalMs));
    }

    console.log('[ConfigService] Primary endpoint timed out after 20s, falling back to secondary-endpoint');
    this._applyBackend('secondary-endpoint');
    return 'secondary-endpoint';
  }

  _applyBackend(type) {
    const prev = this.backendType;
    this.backendType = type;
    this.config = this.getBackendConfig(type);
    if (prev !== type) {
      window.dispatchEvent(new CustomEvent('backendChanged', {
        detail: { oldBackend: prev, newBackend: type, config: this.config }
      }));
    }
  }

  // Method to switch backend programmatically
  switchBackend(newBackendType) {
    console.log('[ConfigService] switchBackend called with:', newBackendType, 'current:', this.backendType);

    if (newBackendType !== this.backendType) {
      // Store preference
      localStorage.setItem('polyglot_backend', newBackendType);
      console.log('[ConfigService] Saved backend preference to localStorage:', newBackendType);
      console.log('[ConfigService] localStorage value after save:', localStorage.getItem('polyglot_backend'));
      
      // Update URL parameter to reflect the change
      const url = new URL(window.location);
      url.searchParams.set('backend', newBackendType);
      window.history.replaceState({}, '', url);
      
      // Reinitialize
      this.init();
      
      // Notify about the change
      window.dispatchEvent(new CustomEvent('backendChanged', {
        detail: { 
          oldBackend: this.backendType,
          newBackend: newBackendType,
          config: this.config 
        }
      }));
      
      console.log('[ConfigService] Backend switch completed successfully');
      return true;
    }
    console.log('[ConfigService] Backend already set to', newBackendType, '- no change needed');
    return false;
  }

  // Generate URLs for switching backends
  getBackendSwitchUrl(targetBackend) {
    const url = new URL(window.location);
    url.searchParams.set('backend', targetBackend);
    return url.toString();
  }

  // Check if local backend is available
  async checkLocalBackendHealth() {
    try {
      const response = await fetch('http://localhost:7870/api/health', {
        method: 'GET',
        mode: 'cors'
      });
      return response.ok;
    } catch (error) {
      console.log('Local backend not available:', error.message);
      return false;
    }
  }

  // Get status info for UI display
  getStatusInfo() {
    return {
      backend: this.backendType,
      name: this.config.name,
      apiUrl: this.config.apiBaseUrl,
      wsUrl: this.config.websocketUrl,
      authRequired: this.config.requiresHfToken,
      isLocal: this.isLocal()
    };
  }
}

// Export singleton instance
export const configService = new ConfigService();
